/**
 * The site-wide settings of this barcoding site, as last read from
 * DockAutomation (`GET /api/barcoding-settings`).
 *
 * One answer for the whole site, set by an admin in the main web app (Admin >
 * Settings > Barcoding). A driver cannot change it, so there is nothing for it
 * on this site's own Settings screen. It is read as a screen opens and at
 * every log-in, and kept for use out of range.
 *
 * The last answer is kept on the tablet, because the screen that needs it is
 * used out of range. A site setting, not anyone's work: it is NOT cleared at
 * a session boundary. A tablet that has never been told uses the defaults,
 * which are the server's own.
 */

const KEY = 'dockautomation.barcodingSettings'

export const DEFAULT_MOVE_HOLD_SECONDS = 5
export const MOVE_HOLD_SECONDS_MIN = 0
export const MOVE_HOLD_SECONDS_MAX = 60

/** Minutes a tablet takes work with no connection (Mark, 2026-10-15; utils/offlineRules.ts). */
export const DEFAULT_OFFLINE_LIMIT_MINUTES = 15
export const OFFLINE_LIMIT_MINUTES_MIN = 1
export const OFFLINE_LIMIT_MINUTES_MAX = 60

export type BarcodingSettings = {
  /** Seconds a finished move stays on Move Pallets before the forks clear. 0 is no hold. */
  moveHoldSeconds: number
  /** Minutes without a connection after which the tablet takes no more work. */
  offlineLimitMinutes: number
}

type Store = Pick<Storage, 'getItem' | 'setItem'>

function storage(): Store | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

/** A whole number of seconds in range, or the default for anything else. */
export function parseMoveHoldSeconds(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value)
    && value >= MOVE_HOLD_SECONDS_MIN && value <= MOVE_HOLD_SECONDS_MAX
    ? value
    : DEFAULT_MOVE_HOLD_SECONDS
}

/** A whole number of minutes in range, or the default for anything else. */
export function parseOfflineLimitMinutes(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value)
    && value >= OFFLINE_LIMIT_MINUTES_MIN && value <= OFFLINE_LIMIT_MINUTES_MAX
    ? value
    : DEFAULT_OFFLINE_LIMIT_MINUTES
}

/** Whatever the server (or the tablet's own copy) said, made safe to use. */
export function parseBarcodingSettings(value: unknown): BarcodingSettings {
  const raw = value && typeof value === 'object' ? value as Record<string, unknown> : {}
  return {
    moveHoldSeconds: parseMoveHoldSeconds(raw.moveHoldSeconds),
    offlineLimitMinutes: parseOfflineLimitMinutes(raw.offlineLimitMinutes),
  }
}

export function loadBarcodingSettings(store: Store | null = storage()): BarcodingSettings {
  try {
    return parseBarcodingSettings(JSON.parse(store?.getItem(KEY) ?? '{}'))
  } catch {
    return parseBarcodingSettings(null)
  }
}

export function saveBarcodingSettings(settings: BarcodingSettings, store: Store | null = storage()): void {
  try {
    store?.setItem(KEY, JSON.stringify(settings))
  } catch {
    // No room, or no storage: the settings still hold for this page.
  }
}
